<script lang="ts">
	import Icon from './Icon.svelte';
	import type { IconName } from '../icons.js';

	type Variant = 'plain' | 'filled' | 'accent' | 'danger' | 'success';

	interface Props {
		icon: IconName;
		/** Обязательна: кнопка без текста непонятна без подписи для чтения с экрана. */
		label: string;
		variant?: Variant;
		/** Размер кнопки; иконка внутри считается от него. */
		size?: number;
		iconSize?: number;
		disabled?: boolean;
		active?: boolean;
		/** Показывать всплывающую подсказку с текстом `label`. */
		showTitle?: boolean;
		class?: string;
		onclick?: (event: MouseEvent) => void;
		/*
		 * Нажатие с удержанием. Нужно микрофону: держат — идёт запись,
		 * отпустили — уходит. Событие мыши для этого не годится, оно приходит
		 * уже после отпускания.
		 */
		onpointerdown?: (event: PointerEvent) => void;
		onpointerup?: (event: PointerEvent) => void;
		onpointermove?: (event: PointerEvent) => void;
		onpointercancel?: (event: PointerEvent) => void;
	}

	let {
		icon,
		label,
		variant = 'plain',
		size = 40,
		iconSize,
		disabled = false,
		active = false,
		showTitle = true,
		class: className = '',
		onclick,
		onpointerdown,
		onpointerup,
		onpointermove,
		onpointercancel
	}: Props = $props();

	let resolvedIconSize = $derived(iconSize ?? Math.round(size * 0.55));
</script>

<button
	class="icon-button {variant} {className}"
	class:active
	style:--button-size="{size}px"
	{disabled}
	aria-label={label}
	aria-pressed={active ? 'true' : undefined}
	title={showTitle ? label : undefined}
	{onclick}
	{onpointerdown}
	{onpointerup}
	{onpointermove}
	{onpointercancel}
>
	<Icon name={icon} size={resolvedIconSize} />
</button>

<style>
	.icon-button {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: var(--button-size);
		height: var(--button-size);
		border-radius: var(--radius-round);
		color: var(--text-secondary);
		transition:
			background var(--duration-fast) var(--ease-standard),
			color var(--duration-fast) var(--ease-standard);
	}

	.icon-button:not(:disabled):hover {
		background: var(--background-hover);
		color: var(--text-primary);
	}

	.icon-button:not(:disabled):active {
		background: var(--background-active);
	}

	.icon-button:disabled {
		opacity: 0.4;
	}

	.icon-button.active {
		color: var(--accent-primary);
	}

	/* --- Виды ------------------------------------------------------------ */
	.filled {
		background: var(--background-secondary);
		color: var(--text-primary);
	}

	.accent {
		background: var(--accent-primary);
		color: var(--text-on-accent);
	}

	.accent:not(:disabled):hover {
		background: var(--accent-hover);
		color: var(--text-on-accent);
	}

	.danger {
		background: var(--status-danger);
		color: #fff;
	}

	.danger:not(:disabled):hover {
		background: var(--status-danger-hover);
		color: #fff;
	}

	.success {
		background: var(--status-success);
		color: #fff;
	}
</style>
